import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { Category, ICategoryDataV3 } from '@df/catalog/category/category';
import { DfModuleImageModule } from '@df/module-image/df-module-image.module';
import { BgColorOptions } from '@df/ui/atomic/atomic-options';
import { DragScrollDirective } from '@df/ui/common/drag-scroll.directive';
import { IfSizeDirective } from '@df/ui/common/if-size.directive';
import { DfUiModule } from '@df/ui/df-ui.module';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { BreadcrumbsComponent } from '../../../meta/breadcrumbs.component';

export namespace NCmsCategoryHeaderBlock {
    export const BLOCK_ID = 'cms-category-header';

    export interface IBlockDataContent {
        filters: IFilter[];
        /**
         * Right hand side image
         */
        sideImage: string;
        backgroundColor?: string;
        showSideImage?: boolean;
    }

    export interface IFilter {
        label: string;
        url: string;
    }
}
@ICmsComponent<CmsCategoryHeaderBlockComponent>({
    id: NCmsCategoryHeaderBlock.BLOCK_ID,
    block: {
        url: NCmsCategoryHeaderBlock.BLOCK_ID
    },
    onDemandOnly: true,
    name: 'Category header',
    fields: [
        <NICmsContentField.IToggleField>{
            key: 'showSideImage',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Image',
            labels: { true: 'Visible', false: 'Hidden' },
            default: true
        },
        {
            type: NICmsContentField.EFieldType.image,
            key: 'sideImage',
            title: 'Side image',
            canShow: (data: NCmsCategoryHeaderBlock.IBlockDataContent) => data.showSideImage,
            description: 'If not set, the Magento category image will be used.'
        },
        <NICmsContentField.IChoiceField>{
            key: 'backgroundColor',
            type: NICmsContentField.EFieldType.choice,
            previewMode: 'color',
            title: 'Background color',
            options: BgColorOptions.filter(color => ['bg-col-w', 'bg-col-51', 'bg-col-52'].includes(color.key as string)),
            default: 'bg-col-w'
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'filters',
            title: 'Filters',
            itemName: 'Filters',
            itemNameField: 'label',
            fields: [
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'label',
                    title: 'Label',
                    default: 'Quick Filter'
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'url',
                    title: 'Link',
                    default: '/'
                }
            ]
        }
    ]
})
@Component({
    imports: [
        BreadcrumbsComponent,
        DfModuleImageModule,
        DfUiModule,
        ICmsClientModule,
        NgTemplateOutlet,
        DragScrollDirective,
        IfSizeDirective
    ],
    selector: 'cms-category-header-block',
    templateUrl: './cms-category-header-block.html',
    styleUrl: 'cms-category-header-block.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CmsCategoryHeaderBlockComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'blockContent'
    })
    content?: NCmsCategoryHeaderBlock.IBlockDataContent;

    @Input()
    category?: Category<ICategoryDataV3>;

    @Input()
    variant?: string;

    get categoryImage(): string | undefined {
        return this.category?.data?.image;
    }

    get categoryName(): string | undefined {
        return this.category?.name;
    }

    get categoryDescription(): string | undefined {
        return this.category?.data?.description;
    }
}
